import {
	DoneRounded,
	KeyboardArrowDownRounded,
	KeyboardArrowUpRounded,
	WarningAmberRounded,
} from "@mui/icons-material";
import {
	Collapse,
	IconButton,
	InputAdornment,
	List,
	styled,
	Typography,
} from "@mui/material";
import { FastField as Field, useField, useFormikContext } from "formik";
import { TextField } from "formik-mui";
import { useMemo, useState } from "react";
import { PercentageEmissionRow } from "src/components/fleet/PercentageEmissionRow";
import { PercentageRow } from "src/components/fleet/PercentageRow";
import type { FleetPercentageFuel } from "src/types";
import { clampIntegerPercentFromInput } from "src/utils/fleet-percentage.utils";

interface Props {
	fuel: FleetPercentageFuel;
	categoryIndex: number;
	fuelIndex: number;
	readOnly: boolean;
}

export function PercentageFuelRow(props: Props) {
	const { fuel, categoryIndex, fuelIndex, readOnly } = props;
	const [open, setOpen] = useState(false);
	const fieldName = `categories.${categoryIndex}.fuels.${fuelIndex}.fuelPercentage`;
	const { setFieldValue } = useFormikContext();

	const [field] = useField<FleetPercentageFuel>({
		name: `categories.${categoryIndex}.fuels.${fuelIndex}`,
	});
	const total = useMemo(
		() =>
			field.value.euros
				.map((euro) => euro.euroPercentage)
				.reduce((a, b) => a + b, 0),
		[field.value.euros],
	);
	const errorDifference = useMemo(() => 100 - total, [total]);
	const errorDisplay =
		errorDifference > 0
			? `-${errorDifference}%`
			: errorDifference < 0
				? `+${-errorDifference}%`
				: null;

	function handleIntegerInputKeyDown(
		event: React.KeyboardEvent<HTMLInputElement>,
	) {
		if ([".", ",", "e", "E", "+", "-"].includes(event.key)) {
			event.preventDefault();
		}
	}

	return (
		<>
			<Styles as="div" $sticky={open}>
				<div>
					<IconButton
						aria-label="expand row"
						size="small"
						onClick={() => setOpen(!open)}
					>
						{open ? (
							<KeyboardArrowUpRounded fontSize="small" />
						) : (
							<KeyboardArrowDownRounded fontSize="small" />
						)}
					</IconButton>
				</div>
				<Typography fontWeight={500} className="label">
					{fuel.fuelTranslation}
					<span
						className={`pill-container ${total !== 100 ? "pill-container--error" : ""}`.trim()}
					>
						{(open || errorDisplay) && (
							<>
								{errorDisplay ? (
									<WarningAmberRounded fontSize="small" />
								) : (
									<DoneRounded fontSize="small" />
								)}{" "}
								{total}% {errorDisplay && `(${errorDisplay})`}
							</>
						)}
					</span>
				</Typography>
				<Field
					name={fieldName}
					component={TextField}
					size="small"
					disabled={readOnly}
					type="number"
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						setFieldValue(
							fieldName,
							clampIntegerPercentFromInput(e.target.value),
						);
					}}
					onKeyDown={handleIntegerInputKeyDown}
					InputProps={{
						endAdornment: <InputAdornment position="end">%</InputAdornment>,
						inputProps: { min: 0, max: 100, step: 1 },
					}}
				/>
			</Styles>
			<Collapse in={open} unmountOnExit>
				<List component="div" disablePadding>
					{fuel.euros.map((euro, euroIndex) => (
						<PercentageEmissionRow
							key={euro.euro}
							emission={euro}
							categoryIndex={categoryIndex}
							fuelIndex={fuelIndex}
							euroIndex={euroIndex}
							readOnly={readOnly}
						/>
					))}
				</List>
			</Collapse>
		</>
	);
}

const Styles = styled(PercentageRow, {
	shouldForwardProp: (prop) => prop !== "$sticky",
})<{ $sticky: boolean }>`
  padding-left: 54px;
  .MuiFormControl-root {
  }

  ${({ $sticky, theme }) =>
		$sticky
			? `
    position: sticky;
    top: 60px;
    z-index: 1;
    background-color: ${theme.palette.background.paper};
  `
			: ""}
`;
